import React from 'react';
import PropTypes from 'prop-types';
import './DateRangeInput.scss';
import { getV5Props } from '../../text/GetV5Props.js';
import { DatePicker } from 'v5/design-system/DatePicker.js';
import { cn } from 'v5/platform/dom/cn';
import { InlineField } from 'v5/platform/react/InlineField.js';
import { ArrowRight } from 'lucide-react';
import { maybe } from '@passionware/monads';
import {
  formatMixedCalendarDay,
  parseMixedCalendarDay
} from 'v1/helpers/mixedDateBoundary';

const DateRangeInput = ({
  className,
  appearance,
  size,
  hideSpacer,
  inputClassname,
  labels = {},
  disabled,
  dateFormat,
  startDate,
  endDate,
  children,
  onValueChange,
  minDate,
  maxDate,
  allowedRanges,
  placeholder,
  placeholderStart,
  placeholderEnd,
  changeOnDebounce = false,
  onIncludeHoursChange,
  allowChangeTimeSelect,
  ...rest
}) => {
  if (rest.onChange) {
    throw new Error(
      'onChange is not supported anymore, use onValueChange instead'
    );
  }

  function preprocessDate(date) {
    return parseMixedCalendarDay(date);
  }
  function postprocessDate(date) {
    if (!date) {
      return null;
    }

    if (typeof date === 'string') {
      throw new Error(
        '!migration! Date is a string, and should not be anymore'
      );
    }

    return formatMixedCalendarDay(date, { dateOnly: !rest.showTimeSelect });
  }

  return (
    <div className="tailwind-root">
      {(!!labels.start || !!labels.end) && (
        <label className="form-label flex! flex-row! items-center gap-2">
          {labels.start ? <div>{labels.start}</div> : null}
          {labels.start && labels.end && <ArrowRight className="size-3" />}
          {labels.end ? <div>{labels.end}</div> : null}
        </label>
      )}
      <InlineField
        tmp_canAcceptChange={value => {
          return value === null || (value.from && value.to);
        }}
        changeOnDebounce={changeOnDebounce}
        value={
          startDate || endDate
            ? {
                from: preprocessDate(startDate),
                to: preprocessDate(endDate)
              }
            : null
        }
        onChange={range => {
          onValueChange?.(
            maybe.mapOrNull(range, range => ({
              from: postprocessDate(range.from),
              to: postprocessDate(range.to)
            }))
          );
        }}
        doNotCompare
      >
        {({ value, onChange, onBlur }) => (
          <DatePicker
            value={value}
            onChange={(value, reason) => {
              switch (reason) {
                case 'input':
                  onChange(value);
                  break;
                default:
                  onChange(value);
                  onBlur?.();
                  break;
              }
            }}
            onBlur={onBlur}
            // popoverExtra={<DatePickerPresets />}
            disabled={disabled}
            startLabel={labels ? labels.start : null}
            endLabel={labels ? labels.end : null}
            mode="range"
            granularity={rest.showTimeSelect ? 'minute' : 'day'}
            allowChangeGranularity={allowChangeTimeSelect}
            onIncludeHoursChange={onIncludeHoursChange}
            maxDate={maxDate}
            minDate={minDate}
            allowedRanges={allowedRanges}
            {...getV5Props({ size, appearance })}
            clearable={rest.required !== true}
            modifiers={rest.highlightDates}
            className={cn('w-full', inputClassname)}
          />
        )}
      </InlineField>
    </div>
  );
};

DateRangeInput.propTypes = {
  className: PropTypes.string,
  inputClassname: PropTypes.string,
  appearance: PropTypes.oneOf(['outline', 'underline', 'silent']),
  size: PropTypes.oneOf(['XS', 'S', 'M']),
  labels: PropTypes.shape({
    start: PropTypes.string,
    end: PropTypes.string
  }),
  disabled: PropTypes.bool,
  showTimeSelect: PropTypes.bool,
  allowChangeTimeSelect: PropTypes.bool,
  dateFormat: PropTypes.string.isRequired, // E.g. 'MM-dd-yyyy'
  startDate: PropTypes.any, // TODO: type // string 2222-00-12 or with time
  endDate: PropTypes.any, // TODO: type
  allowedRanges: PropTypes.arrayOf(
    PropTypes.shape({
      from: PropTypes.instanceOf(Date).isRequired,
      to: PropTypes.instanceOf(Date).isRequired
    })
  ),
  children: PropTypes.any,
  onValueChange: PropTypes.func
};

DateRangeInput.defaultProps = {
  appearance: 'outline',
  size: 'M',
  dateFormat: 'MM-dd-yyyy'
};

export default DateRangeInput;
